/**
 * Utility functions for calendar appointment management and analytics.
 * Optimized for minimal redundant computation and clear structure.
 */

import { format, isSameDay, parseISO } from 'date-fns';
import { bg, de, enUS } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import type { TFunction } from 'i18next';
import type { SupportedLanguage } from '@/i18n';
import type { ReferenceType } from '@floating-ui/react';
import {
  type Appointment,
  type AppointmentWithExternalConflict,
  type CalendarExternalAppointment,
  type CalendarVisibilityMode
} from '@/api/mcp-appointments';
import { getAppointmentClientDisplayLabel } from '../appointmentClientDisplay';

/** Resource filter value for the manage calendar (all resources or a specific id). */
export type CalendarResourceFilter = string | 'ALL';

/**
 * FullCalendar event colors for imported ICS blockers (external calendar events).
 * Applied to background, border, and text in month/week/day views and the legend.
 */
export const EXTERNAL_EVENT_COLORS = {
  backgroundColor: '#f6ffed',
  borderColor: '#237804',
  textColor: '#135200'
} as const;

/**
 * Builds a Floating UI virtual reference from a fixed DOM rectangle snapshot.
 * Used so popups stay anchored when the underlying element scrolls or is removed.
 *
 * @param rect - Bounding rectangle (typically from `Element.getBoundingClientRect()`).
 * @returns A {@link ReferenceType} whose `getBoundingClientRect` returns the frozen rect.
 */
export function createVirtualReferenceFromRect(rect: DOMRectReadOnly): ReferenceType {
  const snapshot = {
    width: rect.width,
    height: rect.height,
    x: rect.x,
    y: rect.y,
    top: rect.top,
    left: rect.left,
    right: rect.right,
    bottom: rect.bottom,
  };

  return {
    getBoundingClientRect: () => snapshot,
  };
}

/**
 * Normalizes a Floating UI reference for appointment or external-event detail popups.
 * DOM elements are converted to virtual references so positioning survives calendar reflows.
 *
 * @param reference - Click target element, existing virtual reference, or `null`/`undefined`.
 * @returns A virtual reference, the input reference unchanged, or `null` when absent.
 */
export function normalizeDetailsReference(reference?: ReferenceType | null): ReferenceType | null {
  if (reference == null) {
    return null;
  }

  if (reference instanceof Element) {
    return createVirtualReferenceFromRect(reference.getBoundingClientRect());
  }

  return reference;
}

const APPOINTMENT_DATE_TIME_LOCALES: Record<SupportedLanguage, Locale> = {
  en: enUS,
  de,
  bg
};

const APPOINTMENT_DATE_TIME_PATTERNS: Record<SupportedLanguage, string> = {
  en: 'MMM d, yyyy HH:mm',
  de: 'd. MMM yyyy, HH:mm',
  bg: 'd MMM yyyy, HH:mm'
};

const APPOINTMENT_TIME_PATTERNS: Record<SupportedLanguage, string> = {
  en: 'HH:mm',
  de: 'HH:mm',
  bg: 'HH:mm'
};

const APPOINTMENT_STATUS_I18N_KEYS: Record<string, string> = {
  SCHEDULED: 'status.scheduled',
  CONFIRMED: 'status.confirmed',
  PENDING: 'status.pending',
  CANCELLED: 'status.cancelled',
  COMPLETED: 'status.completed',
  NO_SHOW: 'status.noShow',
  IN_PROGRESS: 'status.inProgress'
};

/** Global v1 minimum notice for customer change requests (minutes). */
export const CHANGE_REQUEST_MIN_NOTICE_MINUTES = 60;

export function isChangeRequestDeadlinePassed(startTime: string | Date, now: Date = new Date()): boolean {
  const start = typeof startTime === 'string' ? parseISO(startTime) : startTime;
  if (Number.isNaN(start.getTime())) {
    return true;
  }
  const cutoff = new Date(start.getTime() - CHANGE_REQUEST_MIN_NOTICE_MINUTES * 60_000);
  return now > cutoff;
}

export function canCustomerRequestChange(appointment: Appointment): boolean {
  if (appointment.status === 'PENDING') {
    return false;
  }
  if (!['SCHEDULED', 'CONFIRMED'].includes(appointment.status)) {
    return false;
  }
  if (isChangeRequestDeadlinePassed(appointment.startTime)) {
    return false;
  }
  if (appointment.activeChangeRequest?.status === 'PENDING') {
    return false;
  }
  if (appointment.changeRequestEligibility && !appointment.changeRequestEligibility.canSubmit) {
    return false;
  }
  return true;
}

export type CustomerChangeRequestType = 'RESCHEDULE' | 'CANCEL';

export function getCustomerChangeRequestBlockingMessage(
  appointment: Appointment,
  requestType: CustomerChangeRequestType,
  t: TFunction
): string | null {
  if (requestType === 'CANCEL' && appointment.status === 'CANCELLED') {
    return t('client.changeRequest.alreadyCancelled', { ns: 'dashboard' });
  }
  if (!canCustomerRequestChange(appointment)) {
    if (isChangeRequestDeadlinePassed(appointment.startTime)) {
      return t('client.changeRequest.deadlineBlocked', { ns: 'dashboard' });
    }
    if (appointment.activeChangeRequest?.status === 'PENDING') {
      return t('client.changeRequest.activeRequestExists', { ns: 'dashboard' });
    }
    if (appointment.changeRequestEligibility?.reasons[0]) {
      return appointment.changeRequestEligibility.reasons[0];
    }
    return t('client.changeRequest.submitFailed', { ns: 'dashboard' });
  }
  return null;
}

/**
 * Formats an ISO appointment or external-event timestamp for display in detail popups.
 *
 * @param dateString - ISO 8601 date-time string from the appointments API.
 * @param language - Active i18n language code (`en`, `de`, `bg`).
 * @returns Human-readable string (e.g. `14. Apr. 2026, 15:00` in German).
 */
export function formatAppointmentDateTime(dateString: string, language = 'en'): string {
  const lng = (language in APPOINTMENT_DATE_TIME_LOCALES ? language : 'en') as SupportedLanguage;
  return format(parseISO(dateString), APPOINTMENT_DATE_TIME_PATTERNS[lng], {
    locale: APPOINTMENT_DATE_TIME_LOCALES[lng]
  });
}

/** Formats a start/end range, omitting the date on the end when it matches the start day. */
export function formatAppointmentTimeRange(
  startString: string,
  endString: string | null | undefined,
  language = 'en'
): string {
  const lng = (language in APPOINTMENT_DATE_TIME_LOCALES ? language : 'en') as SupportedLanguage;
  const locale = APPOINTMENT_DATE_TIME_LOCALES[lng];
  const start = parseISO(startString);
  if (Number.isNaN(start.getTime())) {
    return startString;
  }
  const startFormatted = format(start, APPOINTMENT_DATE_TIME_PATTERNS[lng], { locale });
  if (!endString) {
    return startFormatted;
  }
  const end = parseISO(endString);
  if (Number.isNaN(end.getTime())) {
    return startFormatted;
  }
  if (isSameDay(start, end)) {
    return `${startFormatted} – ${format(end, APPOINTMENT_TIME_PATTERNS[lng], { locale })}`;
  }
  return `${startFormatted} – ${format(end, APPOINTMENT_DATE_TIME_PATTERNS[lng], { locale })}`;
}

/**
 * Returns a localized label for an appointment status enum value.
 *
 * @param status - Appointment status (uppercase, e.g. `SCHEDULED`).
 * @param t - i18n translate function.
 * @returns Localized status label from the `common` namespace, or a fallback for unknown values.
 */
export function getAppointmentStatusLabel(status: string, t: TFunction): string {
  const key = APPOINTMENT_STATUS_I18N_KEYS[status];
  if (key) {
    return t(key, { ns: 'common' });
  }
  return status.replace('_', ' ');
}

/** Localized label for reschedule/cancel change-request status. */
export function getChangeRequestStatusLabel(status: string, t: TFunction): string {
  if (status === 'APPROVED') {
    return t('appointmentComponents.changeRequest.statusApproved', { ns: 'manage' });
  }
  if (status === 'REJECTED') {
    return t('appointmentComponents.changeRequest.statusRejected', { ns: 'manage' });
  }
  if (status === 'CANCELLED') {
    return t('status.cancelled', { ns: 'common' });
  }
  return getAppointmentStatusLabel(status, t);
}

/**
 * Maps appointment status to a hex background color for FullCalendar events.
 *
 * @param status - Appointment status (uppercase, e.g. `CONFIRMED`).
 * @returns Hex color string; defaults to blue when status is unknown.
 */
export const getStatusBackgroundColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    SCHEDULED: '#1890ff',   // Blue for scheduled
    CONFIRMED: '#52c41a',   // Green for confirmed
    PENDING: '#722ed1',     // Purple for pending change request
    CANCELLED: '#ff4d4f',   // Red for cancelled
    COMPLETED: '#8c8c8c',   // Gray for completed
    NO_SHOW: '#fa8c16',     // Orange for no-show
    IN_PROGRESS: '#faad14'  // Yellow for in-progress
  };
  return colorMap[status] || '#1890ff';
};

/** Lighter fill for service booking events (same status hue, less saturated). */
export function getServiceBookingMutedBackground(status: string, mixWithWhite = 0.88): string {
  const hex = getStatusBackgroundColor(status).replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const mix = (channel: number) => Math.round(channel + (255 - channel) * mixWithWhite);
  const toPart = (n: number) => mix(n).toString(16).padStart(2, '0');
  return `#${toPart(r)}${toPart(g)}${toPart(b)}`;
}

const CALENDAR_APPOINTMENT_TIME_LOCALES: Record<SupportedLanguage, Locale> = {
  en: enUS,
  de,
  bg
};

/** Service-backed booking (shows service name and reserved resources on the calendar card). */
export function isServiceCalendarBooking(appointment: Appointment): boolean {
  return Boolean(appointment.serviceId ?? appointment.service?.name);
}

export function getCalendarAppointmentHeadline(appointment: Appointment): string {
  if (appointment.service?.name) {
    return appointment.service.name;
  }
  return '';
}

/**
 * Primary allocated resource display name for calendar chips.
 * Skips serializer fallbacks that reuse the raw resourceId as the name.
 */
export function getCalendarAppointmentResourceLabel(appointment: Appointment): string | null {
  const resources = appointment.allocatedResources ?? [];
  if (resources.length === 0) return null;
  const primary = resources.find((r) => r.isPrimary) ?? resources[0];
  const name = primary?.name?.trim();
  if (!name) return null;
  if (name === primary.resourceId) return null;
  return name;
}

/** Who the calendar event card is labeled for (affects primary chip text). */
export type CalendarEventAudience = 'staff' | 'customer';

export function getCalendarAppointmentClientLabel(
  appointment: Appointment,
  pendingLabel: string,
  userDisplayById?: Map<string, { label: string }> | Record<string, { label: string }>
): string {
  return getAppointmentClientDisplayLabel(appointment, pendingLabel, userDisplayById);
}

export function formatCalendarAppointmentTimeRange(
  startTime: string,
  endTime: string,
  language: SupportedLanguage = 'en'
): string {
  const locale = CALENDAR_APPOINTMENT_TIME_LOCALES[language] ?? enUS;
  const start = parseISO(startTime);
  const end = parseISO(endTime);
  return `${format(start, 'HH:mm', { locale })} – ${format(end, 'HH:mm', { locale })}`;
}

/** Appointments shorter than this use compact single-line layout in week/day views. */
export const SHORT_TIME_GRID_EVENT_THRESHOLD_MINUTES = 45;

/** Minimum slot length before resource tags are shown in week/day view. */
const TIME_GRID_DETAILED_BASE_MINUTES = 75;
const TIME_GRID_TAG_ROW_MINUTES = 30;

export type TimeGridAppointmentLayout = 'compact' | 'standard' | 'detailed';

export function getEventDurationMinutes(startTime: string, endTime: string): number {
  const start = parseISO(startTime);
  const end = parseISO(endTime);
  return Math.round((end.getTime() - start.getTime()) / (60 * 1000));
}

export function getTimeGridEventDurationMinutes(
  start: Date | null | undefined,
  end: Date | null | undefined
): number {
  if (!start || !end) return 0;
  return Math.round((end.getTime() - start.getTime()) / (60 * 1000));
}

export function isShortTimeGridAppointment(appointment: Appointment): boolean {
  return getCalendarAppointmentDurationMinutes(appointment) < SHORT_TIME_GRID_EVENT_THRESHOLD_MINUTES;
}

export function isShortTimeGridEvent(startTime: string, endTime: string): boolean {
  return getEventDurationMinutes(startTime, endTime) < SHORT_TIME_GRID_EVENT_THRESHOLD_MINUTES;
}

export function isTimeGridCalendarView(viewType: string): boolean {
  return viewType === 'timeGridWeek' || viewType === 'timeGridDay';
}

/** Visual slot length on the calendar grid (start/end), not service policy duration. */
export function getCalendarAppointmentDurationMinutes(appointment: Appointment): number {
  return getEventDurationMinutes(appointment.startTime, appointment.endTime);
}

/**
 * Picks week/day event density from slot length.
 * Compact: single line. Standard: title + meta. Detailed: month-view card with resource tags.
 */
export function getTimeGridAppointmentLayout(
  appointment: Appointment,
  eventStart?: Date | null,
  eventEnd?: Date | null
): TimeGridAppointmentLayout {
  const minutes = eventStart && eventEnd
    ? getTimeGridEventDurationMinutes(eventStart, eventEnd)
    : getCalendarAppointmentDurationMinutes(appointment);

  if (minutes < SHORT_TIME_GRID_EVENT_THRESHOLD_MINUTES) {
    return 'compact';
  }

  if (!isServiceCalendarBooking(appointment)) {
    return 'standard';
  }

  const tagCount = appointment.allocatedResources?.length ?? 0;
  if (tagCount === 0) {
    return 'standard';
  }

  const tagRows = Math.ceil(tagCount / 2);
  const minMinutesForTags = TIME_GRID_DETAILED_BASE_MINUTES
    + Math.max(0, tagRows - 1) * TIME_GRID_TAG_ROW_MINUTES;

  return minutes >= minMinutesForTags ? 'detailed' : 'standard';
}

/** Solid status colors for the combined manage calendar legend (resource-only bookings). */
export const CALENDAR_STATUS_LEGEND_ITEMS: Array<{
  status: string;
  labelKey: string;
  dashed?: boolean;
}> = [
  { status: 'SCHEDULED', labelKey: 'appointmentComponents.legend.scheduled', dashed: true },
  { status: 'CONFIRMED', labelKey: 'appointmentComponents.legend.confirmed' },
  { status: 'PENDING', labelKey: 'appointmentComponents.legend.pending' },
  { status: 'IN_PROGRESS', labelKey: 'appointmentComponents.legend.inProgress' },
  { status: 'COMPLETED', labelKey: 'appointmentComponents.legend.completed' },
  { status: 'CANCELLED', labelKey: 'appointmentComponents.legend.cancelled' },
  { status: 'NO_SHOW', labelKey: 'appointmentComponents.legend.noShow' }
];

/**
 * Maps appointment status to an Ant Design `Tag` color token.
 *
 * @param status - Appointment status (uppercase, e.g. `SCHEDULED`).
 * @returns Ant Design tag color name; defaults to `default` when status is unknown.
 */
export const getStatusTagColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    SCHEDULED: 'blue',
    CONFIRMED: 'green',
    PENDING: 'purple',
    COMPLETED: 'default',
    CANCELLED: 'red',
    NO_SHOW: 'volcano',
    IN_PROGRESS: 'orange'
  };
  return colorMap[status] || 'default';
};

/**
 * Aggregate appointment statistics for the manage calendar header and filters.
 */
export interface AppointmentStats {
  /** Total appointments in the current filtered set. */
  total: number;
  /** Count with status `SCHEDULED`. */
  scheduled: number;
  /** Count with status `CONFIRMED`. */
  confirmed: number;
  /** Count with status `IN_PROGRESS`. */
  inProgress: number;
  /** Count with status `COMPLETED`. */
  completed: number;
  /** Count with status `CANCELLED`. */
  cancelled: number;
  /** Count with status `NO_SHOW`. */
  noShow: number;
  /** Raw counts keyed by status string. */
  byStatus: Record<string, number>;
  /** Scheduled or confirmed appointments starting today (local time). */
  upcomingToday: number;
  /** Scheduled or confirmed appointments starting within the next 7 days (local time). */
  upcomingThisWeek: number;
  /** Mean duration in minutes across all appointments in the set. */
  averageDuration: number;
}

/**
 * Builds the display label for an imported ICS event on the calendar.
 *
 * @param resourceName - Human-readable resource name.
 * @param normalizedSummary - Event title from the feed (may be redacted in privacy mode).
 * @param t - i18n translate function (`manage` namespace).
 * @returns Label in the form `Resource — Summary`, with a fallback when summary is empty.
 */
export function formatExternalEventLabel(
  resourceName: string,
  normalizedSummary: string | null | undefined,
  t: TFunction
): string {
  const detail =
    normalizedSummary?.trim() ||
    t('appointmentComponents.externalEvent.notAvailable');
  return `${resourceName} — ${detail}`;
}

/** One external-vs-SA overlap for the manage calendar conflict alert and day markers. */
export interface CalendarConflictAlertItem {
  externalLabel: string;
  appointmentName: string;
  weekday: string;
  date: string;
  timeRange: string;
  /** Local calendar day (`yyyy-MM-dd`) of the overlap start, for month-view badges. */
  overlapDayKey: string;
}

/**
 * Flattens all `externalConflicts` on appointments into alert/list items.
 *
 * @param appointments - Appointments from the external list API (unfiltered by status/user).
 * @param resourceNameById - Resource display names keyed by id.
 * @param t - i18n translate function (`manage` namespace).
 */
export function collectExternalConflictItems(
  appointments: AppointmentWithExternalConflict[],
  resourceNameById: Record<string, string>,
  t: TFunction
): CalendarConflictAlertItem[] {
  const items: CalendarConflictAlertItem[] = [];

  for (const apt of appointments) {
    const conflicts = apt.externalConflicts ?? [];
    if (conflicts.length === 0) continue;

    const resourceName =
      resourceNameById[apt.resourceId] ??
      t('appointmentComponents.resourceFallback', { id: apt.resourceId });
    const appointmentName =
      apt.anonymousName ||
      (isServiceCalendarBooking(apt) ? getCalendarAppointmentHeadline(apt) : null) ||
      resourceName;

    for (const conflict of conflicts) {
      const overlapStart = parseISO(conflict.overlapStartTime);
      const overlapEnd = parseISO(conflict.overlapEndTime);
      const externalDetail =
        conflict.normalizedSummary?.trim() ||
        t('appointmentComponents.externalEvent.unavailable');

      items.push({
        externalLabel: `${resourceName} — ${externalDetail}`,
        appointmentName,
        weekday: format(overlapStart, 'EEEE'),
        date: format(overlapStart, 'MMMM d, yyyy'),
        timeRange: `${format(overlapStart, 'HH:mm')}–${format(overlapEnd, 'HH:mm')}`,
        overlapDayKey: format(overlapStart, 'yyyy-MM-dd')
      });
    }
  }

  return items;
}

/**
 * Maps an imported external appointment to a FullCalendar event input object.
 *
 * @param external - External blocker from the appointments list API.
 * @param resourceName - Human-readable resource name for the event title.
 * @param t - i18n translate function (`manage` namespace).
 * @param visibilityMode - Feed visibility; `FULL_VISIBILITY` adds a clickable CSS class.
 * @returns FullCalendar-compatible event with `extendedProps` for click handling and popups.
 */
export function toFullCalendarExternalEvent(
  external: CalendarExternalAppointment,
  resourceName: string,
  t: TFunction,
  visibilityMode: CalendarVisibilityMode = 'FULL_VISIBILITY'
) {
  const label = formatExternalEventLabel(resourceName, external.normalizedSummary, t);
  const className = ['fc-event-external'];
  if (visibilityMode === 'FULL_VISIBILITY') {
    className.push('fc-event-external-clickable');
  }

  return {
    id: `ext-${external.id}`,
    title: label,
    start: external.startTime,
    end: external.endTime,
    color: EXTERNAL_EVENT_COLORS.borderColor,
    contrastColor: EXTERNAL_EVENT_COLORS.textColor,
    editable: false,
    className: className.join(' '),
    extendedProps: {
      eventKind: 'external',
      externalId: external.id,
      calendarFeedId: external.calendarFeedId,
      resourceId: external.resourceId,
      startTime: external.startTime,
      endTime: external.endTime,
      status: external.status,
      externalUid: external.externalUid,
      normalizedSummary: external.normalizedSummary ?? null,
      normalizedLocation: external.normalizedLocation ?? null,
      visibilityMode,
      label
    }
  };
}

/**
 * Expands the visible calendar range by one day on each side for list API queries.
 * Reduces missed appointments near timezone boundaries and view edges.
 *
 * @param start - Visible range start from FullCalendar `datesSet`.
 * @param end - Visible range end (exclusive) from FullCalendar `datesSet`.
 * @returns ISO 8601 `startDate` and `endDate` strings for `ListAppointments` params.
 */
export function padVisibleRangeForApi(start: Date, end: Date): { startDate: string; endDate: string } {
  const padMs = 24 * 60 * 60 * 1000;
  return {
    startDate: new Date(start.getTime() - padMs).toISOString(),
    endDate: new Date(end.getTime() + padMs).toISOString()
  };
}

/**
 * Computes aggregate appointment statistics in a single pass over the input list.
 *
 * @param appointments - Appointments to summarize; optional `duration` (minutes) per item.
 * @returns {@link AppointmentStats} used by `CalendarStats` and related UI.
 */
export const calculateStats = (appointments: (Appointment & { duration?: number })[]): AppointmentStats => {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
  const weekEnd = new Date(todayStart.getTime() + 7 * 24 * 60 * 60 * 1000);

  // Stats accumulators
  let scheduled = 0, confirmed = 0, inProgress = 0, completed = 0, cancelled = 0, noShow = 0;
  let upcomingToday = 0, upcomingThisWeek = 0, totalDuration = 0;
  const byStatus: Record<string, number> = {};

  // Loop once through all appointments for all counters: faster for large lists
  for (const a of appointments) {
    totalDuration += a.duration || 0;

    // Count by normalized status
    byStatus[a.status] = (byStatus[a.status] || 0) + 1;
    switch (a.status) {
      case 'SCHEDULED': scheduled++; break;
      case 'CONFIRMED': confirmed++; break;
      case 'IN_PROGRESS': inProgress++; break;
      case 'COMPLETED': completed++; break;
      case 'CANCELLED': cancelled++; break;
      case 'NO_SHOW': noShow++; break;
      default: /* ignore */ break;
    }

    // Only compute date logic for scheduled/confirmed and when startTime is available
    if ((a.status === 'SCHEDULED' || a.status === 'CONFIRMED') && a.startTime) {
      const apptStart = parseISO(a.startTime);
      if (apptStart >= todayStart && apptStart < todayEnd) {
        upcomingToday++;
      }
      if (apptStart >= todayStart && apptStart < weekEnd) {
        upcomingThisWeek++;
      }
    }
  }

  return {
    total: appointments.length,
    scheduled,
    confirmed,
    inProgress,
    completed,
    cancelled,
    noShow,
    byStatus,
    upcomingToday,
    upcomingThisWeek,
    averageDuration: appointments.length > 0 ? totalDuration / appointments.length : 0
  };
};

/** Whether a tenant can manually mark customer no-show from the details modal. */
export function canMarkAppointmentNoShow(appointment: { status: string; startTime: string }): boolean {
  const hasStarted = new Date(appointment.startTime).getTime() <= Date.now();
  const eligibleStatus = appointment.status === 'IN_PROGRESS' || appointment.status === 'COMPLETED';
  return hasStarted && eligibleStatus;
}
